import React, { Component, type ReactNode } from 'react';
import { Card, CardContent } from './card';
import { Button } from './Button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: () => void;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error Boundary caught error:', { error, errorInfo });
    this.props.onError?.();
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center min-h-[200px] text-center">
              <AlertTriangle className="w-8 h-8 text-destructive mb-3" />
              <h4 className="text-sm font-medium mb-1">Component Error</h4>
              <p className="text-xs text-muted-foreground mb-3 max-w-sm">
                {this.state.error?.message || 'Something went wrong'}
              </p>
              <Button size="sm" onClick={this.handleReset}>
                <RefreshCw className="w-3 h-3 mr-1" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
